// src/services/nodeService.js

import * as api from './api';

/**
 * Convert a network name/string ID to its numeric ID
 * @param {string|number} networkId - Network identifier
 * @returns {number} Numeric network ID
 */
function convertNetworkIdToNumeric(networkId) {
  // If it's already a number, return it
  if (!isNaN(parseInt(networkId))) {
    return parseInt(networkId);
  }
  
  // Map network names to their numeric IDs
  // Update these values to match your actual database IDs
  const networkMap = {
    'ethereum': 1,
    'bsc': 2,
    'solana': 3,
    'polygon': 4
  };
  
  return networkMap[networkId.toLowerCase()] || 1; // Default to 1 if not found
}



/**
 * Updated getWalletNodes to use wallet_applications endpoint as per API documentation
 */
export const getWalletNodes = async (walletAddress, blockchainNetworkId, walletType) => {
  try {
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Use the correct wallet_applications endpoint as per API documentation
    const response = await fetch('https://api.aeronyx.network/api/node-applications/wallet_applications/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: blockchainNetworkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch nodes: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch user nodes');
    }
    
    // Extract nodes from the response according to the API documentation
    const nodes = result.data?.applications || [];
    
    return {
      success: true,
      nodes: nodes
    };
  } catch (error) {
    console.error('Error in getWalletNodes:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch user nodes',
      nodes: []
    };
  }
}



/**
 * Fetch active nodes sorted by tier level
 * @param {string} walletAddress - User's wallet address for authentication
 * @param {number|string} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Wallet type identifier
 * @returns {Promise<Object>} Response with sorted active nodes
 */
export const getActiveNodes = async (walletAddress, blockchainNetworkId, walletType) => {
  try {
    // Convert network name to numeric ID if needed
    const networkId = convertNetworkIdToNumeric(blockchainNetworkId);
    
    console.log("Using converted network ID:", networkId, "from original:", blockchainNetworkId);
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      networkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Fetch active nodes
    const response = await fetch('https://api.aeronyx.network/api/nodes/active/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch active nodes');
    }
    
    // Sort nodes by tier level (4, 3, 2, 1)
    const nodes = result.data && Array.isArray(result.data.nodes) ? result.data.nodes : [];
    
    // Group nodes by tier for easier access
    const nodesByTier = {
      apex: nodes.filter(node => node.tier_id === 'apex' || node.tier_level === 4),
      master: nodes.filter(node => node.tier_id === 'master' || node.tier_level === 3),
      super: nodes.filter(node => node.tier_id === 'super' || node.tier_level === 2),
      standard: nodes.filter(node => node.tier_id === 'standard' || node.tier_level === 1)
    };
    
    return {
      success: true,
      nodes: nodes,
      nodesByTier: nodesByTier
    };
  } catch (error) {
    console.error('Error in getActiveNodes:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch active nodes',
      nodes: [],
      nodesByTier: { apex: [], master: [], super: [], standard: [] }
    };
  }
};

/**
 * Select a node for RWA purchase
 * @param {string} walletAddress - User's wallet address
 * @param {number|string} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Wallet type
 * @param {string} nodeId - ID of the selected node
 * @param {string} productId - ID of the product being purchased
 * @returns {Promise<Object>} Response with selection status
 */
export const selectNodeForPurchase = async (walletAddress, blockchainNetworkId, walletType, nodeId, productId) => {
  try {
    // Convert network name to numeric ID if needed
    const networkId = convertNetworkIdToNumeric(blockchainNetworkId);
    
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      networkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Send node selection
    const response = await fetch('https://api.aeronyx.network/api/nodes/select-for-purchase/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType,
        node_id: nodeId,
        product_id: productId
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to select node for purchase');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in selectNodeForPurchase:', error);
    return {
      success: false,
      message: error.message || 'Failed to select node for purchase'
    };
  }
};

/**
 * Verify purchase eligibility
 * @param {string} walletAddress - User's wallet address
 * @param {number|string} blockchainNetworkId - Blockchain network ID or name
 * @param {string} productId - Product ID
 * @returns {Promise<Object>} Response with eligibility status
 */
export const verifyPurchaseEligibility = async (walletAddress, blockchainNetworkId, productId) => {
  try {
    // Convert network name to numeric ID if needed
    const networkId = convertNetworkIdToNumeric(blockchainNetworkId);
    
    const response = await fetch('https://api.aeronyx.network/api/products/verify-eligibility/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: networkId,
        product_id: productId
      }),
    });
    
    const result = await response.json();
    
    return {
      success: result.success,
      message: result.message,
      isEligible: result.success && result.data?.isEligible,
      requiredTier: result.data?.requiredTier,
      reason: result.data?.reason
    };
  } catch (error) {
    console.error('Error in verifyPurchaseEligibility:', error);
    return {
      success: false,
      message: error.message || 'Failed to verify purchase eligibility',
      isEligible: false
    };
  }
};

export default {
  getActiveNodes,
  selectNodeForPurchase,
  verifyPurchaseEligibility
};
